import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createSafeContext, deepMerge } from '@mantine/core';
import { useDisclosure, useStateHistory } from '@mantine/hooks';
import { IconChevronLeft } from 'ui-icons';

import { ActionIcon } from '../../../Buttons';
import { Group } from '../../../Layout/Group';
import { ModalProps } from '../Modal';
import {
  CreateModalsFactory,
  ModalIds,
  ModalTitleWithBackButtonProps,
  ModalsContext,
  ModalsMeta,
  ModalsProvider,
  UseModalsProvider,
} from './useModals.types';

/**
 *
 * Wraps the modals provider with a provider to ensure that the providers data
 * can always be reset
 */
interface ModalsDataProviderProps extends Pick<ModalsContext, 'defaultOpen' | 'meta'> {
  children: (ctx: Omit<ModalsContext, 'ids'>) => React.ReactNode;
  /** Called when the Modal history goes from a value to undefined, signaling the closing of the modals.  */
  onEmptyHistory?: () => void;
}
const ModalsDataProvider = ({
  children,
  meta,
  defaultOpen,
  onEmptyHistory,
}: ModalsDataProviderProps) => {
  const [_meta, setMeta] = useState(meta);
  const [value, handlers, history] = useStateHistory<string>(defaultOpen || '');

  /**
   * Merge the meta with the new meta
   */
  const deepMergeMeta = useCallback(() => {
    setMeta((prev) => deepMerge(prev, meta));
  }, [meta]);

  /**
   * Merge the meta with the meta provided by the parent container on meta change
   */
  useEffect(() => {
    deepMergeMeta();
  }, [deepMergeMeta]);

  // When there was history, and the new value is closed, call the onEmptyHistory
  useEffect(() => {
    // Check if the history has more than one value, initial is one value
    const hasHistory = history.history.length > 1;

    if (!value && hasHistory) {
      // Reset the meta to the latest meta provided by the parent container
      setMeta(meta);
      handlers.reset();
      onEmptyHistory?.();
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return children({
    setMeta,
    defaultOpen,
    historyValue: value,
    handlers,
    history,
    meta: _meta,
    defaultMeta: meta,
  });
};

const createModalsFactory: CreateModalsFactory = <
  TModalIds extends ModalIds,
  TMeta extends ModalsMeta,
>(
  ModalIds
) => {
  const [_Provider, _useProviderContext] = createSafeContext<ModalsContext<ModalIds>>(
    'Modals Factory Context component was not found in tree'
  );

  const Provider: ModalsProvider = ({ children, meta = {}, defaultOpen, onClose }) => {
    return (
      <ModalsDataProvider
        meta={meta}
        defaultOpen={defaultOpen}
        onEmptyHistory={() => {
          onClose?.();
        }}
      >
        {({ historyValue, handlers, history, meta, setMeta, defaultOpen, defaultMeta }) => (
          <_Provider
            value={{
              historyValue,
              handlers,
              history,
              meta,
              setMeta,
              defaultOpen,
              defaultMeta,
              ids: ModalIds,
            }}
          >
            {children}
          </_Provider>
        )}
      </ModalsDataProvider>
    );
  };

  const useProvider: UseModalsProvider<TModalIds, TMeta> = ({ id, onOpened, onClosed }) => {
    const idRef = useRef(id);
    const [opened, handlers] = useDisclosure();
    const ctx = _useProviderContext();

    const openModalById = useCallback(
      (modalId) => {
        ctx.handlers.set(modalId);
      },
      [ctx.handlers]
    );

    const closeModalById = useCallback(
      (modalId) => {
        ctx.handlers.set(modalId);
      },
      [ctx.handlers]
    );

    const closeAllModals = useCallback(() => {
      ctx.handlers.set('');
    }, [ctx.handlers]);

    const nextModalId = useMemo(() => {
      const currentIndex = ctx.ids.indexOf(ctx.historyValue);
      if (currentIndex === -1) return null;
      const nextIndex = currentIndex + 1;
      if (nextIndex >= ctx.ids.length) return null;
      return ctx.ids[nextIndex];
    }, [ctx.historyValue, ctx.ids]);

    const openNextStep = useCallback(() => {
      if (nextModalId) ctx.handlers.set(nextModalId);
    }, [ctx.handlers, nextModalId]);

    const openPreviousStep = useCallback(() => {
      handlers.close();
      ctx.handlers.back();
    }, [ctx.handlers, handlers]);

    const openForward = useCallback(() => {
      handlers.close();
      ctx.handlers.back();
    }, [ctx.handlers, handlers]);

    const openBack = useCallback(() => {
      handlers.close();
      ctx.handlers.back();
    }, [ctx.handlers, handlers]);

    // Local handlers

    const close = useCallback(() => {
      handlers.close();
      ctx.handlers.set('');
    }, [ctx.handlers, handlers]);

    const open = useCallback(() => {
      if (!idRef.current) return;
      ctx.handlers.set(idRef.current);
    }, [ctx.handlers]);

    const toggle = useCallback(() => {
      handlers.toggle();
    }, [handlers]);

    /** Callback Handlers */
    const resetMeta = useCallback(() => {
      ctx.setMeta(ctx.defaultMeta);
    }, [ctx]);

    const callBackHandlers = useMemo(() => {
      return {
        resetMeta,
        setMeta: ctx.setMeta,
      };
    }, [resetMeta, ctx.setMeta]);

    /**
     * Handle the history change
     */
    useEffect(() => {
      /**  Check if the current modal id is the same as the id of the consuming modal */
      const handleHistoryChange = () => {
        if (ctx.historyValue == idRef.current) {
          handlers.open();
          onOpened?.(callBackHandlers);
        } else if (opened) {
          handlers.close();
          onClosed?.(callBackHandlers);
        }
      };
      handleHistoryChange();
      // eslint-disable-next-line react-hooks/exhaustive-deps -- Change should only happen when history changes
    }, [ctx.historyValue]);

    const props: Pick<ModalProps, 'transitionProps'> = useMemo(
      () => ({
        transitionProps: { exitDuration: 0 },
      }),
      []
    );

    return {
      openModalById,
      closeModalById,
      closeAllModals,
      opened,
      openNextStep,
      openPreviousStep,
      openForward,
      openBack,
      handlers,
      close,
      open,
      toggle,
      nextModalId,
      props,
      meta: ctx.meta as TMeta,
      setMeta: ctx.setMeta,
      currentModalId: ctx.historyValue,
      history: ctx.history,
    };
  };

  const ModalTitleWithBackButton = ({
    children,
    groupProps,
    actionIconProps,
    ...rest
  }: ModalTitleWithBackButtonProps) => {
    const { openBack, history, currentModalId } = useProvider({});

    /**
     * Filter out modals that did not actually open in the history
     * and the current modal is not the current one
     */
    const filteredHistory = useMemo(() => {
      return history.history.filter((id) => id !== '' || id === currentModalId);
    }, [history, currentModalId]);

    /**
     * Only show the back button if there is more than one modal in the history
     * and the current modal is not current one
     */
    const isBackButtonVisible = useMemo(() => {
      return filteredHistory.length > 1 && currentModalId !== filteredHistory[0];
    }, [filteredHistory, currentModalId]);

    return (
      <Group wrap="nowrap" tt={rest.tt ?? 'capitalize'} {...groupProps}>
        {isBackButtonVisible && (
          <ActionIcon onClick={openBack} color="gray.7" variant="subtle" {...actionIconProps}>
            <IconChevronLeft />
          </ActionIcon>
        )}
        {children}
      </Group>
    );
  };
  return {
    Provider,
    useProvider,
    ModalTitleWithBackButton,
  };
};
export { createModalsFactory };
